export type NumberValueInput = number | string | null | undefined;

interface FormatNumberOptions {
  fractionDigits?: number;
  locale?: string;
}

function isEmptyValue(value: NumberValueInput) {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'number' && !Number.isFinite(value)) ||
    (typeof value === 'string' && value.trim() === '')
  );
}

function resolveNumber(value: NumberValueInput) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const numberValue = Number(value);

    return Number.isFinite(numberValue) ? numberValue : null;
  }

  return null;
}

function getUserLocale() {
  return typeof navigator === 'undefined' ? undefined : navigator.language;
}

function format(
  value: NumberValueInput,
  options: Intl.NumberFormatOptions,
  locale?: string
): string | null {
  if (isEmptyValue(value)) {
    return null;
  }

  const number = resolveNumber(value);

  if (number === null) {
    return null;
  }

  return new Intl.NumberFormat(locale ?? getUserLocale(), options).format(
    number
  );
}

export function formatNumber(
  value: NumberValueInput,
  { fractionDigits, locale }: FormatNumberOptions = {}
) {
  const precisionOptions =
    fractionDigits === undefined
      ? {}
      : {
          maximumFractionDigits: fractionDigits,
          minimumFractionDigits: fractionDigits,
        };

  return format(value, precisionOptions, locale);
}

export function formatCurrency(
  value: NumberValueInput,
  currency: string,
  locale?: string
): string | null {
  return format(
    value,
    { style: 'currency', currency, currencyDisplay: 'code' },
    locale
  );
}
